import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getActiveSpace } from 'spaces/index';
import { openModal } from 'modal/index';
import { getAuthUserAuthorities, getIsAdmin } from 'auth/store/auth/selectors';
import { getUsersById } from 'auth/store/users/selectors';
import { addFavorite, fetchFavorites, removeFavorite } from 'favorites/store/favorites/actions';
import { getFavoriteIds } from 'favorites/store/favorites/selectors';
import { uncacheDocumentOffline } from 'offline/store/actions';
import { getOfflineDocumentIds } from 'offline/store/selectors';
import {
  getActiveFolder,
  getActiveFolderChildren,
  getActiveFolderChildrenIsLoading,
} from '../../store/folders/selectors';
import { fetchChildren, setEditingFolderId } from '../../store/folders/actions';
import { fetchDocumentsByActiveFolder, setActiveDocumentId, cloneDocument } from '../../store/documents/actions';
import { setSelectedData } from '../../store/fileexplorer/actions';
import { getActiveFolderDocuments, getActiveFolderDocumentsIsLoading } from '../../store/documents/selectors';
import FileExplorer from '../../components/FileExplorer/FileExplorer';
import { getTagsById } from '../../store/tags/selectors';
import FileExplorerBulkActions from '../FileExplorerBulkActions';
import { fetchAllTags } from '../../store/tags/actions';
import FileExplorerTableSkeleton from '../../components/FileExplorerTableSkeleton';

function ClassicalFileExplorer() {
  const dispatch = useDispatch();
  const usersById = useSelector(getUsersById);
  const tagsById = useSelector(getTagsById);
  const space = useSelector(getActiveSpace);
  const folder = useSelector(getActiveFolder);
  const folders = useSelector(getActiveFolderChildren);
  const documents = useSelector(getActiveFolderDocuments);
  const favoriteIds = useSelector(getFavoriteIds);
  const offlineDocumentIds = useSelector(getOfflineDocumentIds);
  const isAdmin = useSelector(getIsAdmin);
  const authorities = useSelector(getAuthUserAuthorities);
  const documentsAreLoading = useSelector(getActiveFolderDocumentsIsLoading);
  const foldersAreLoading = useSelector(getActiveFolderChildrenIsLoading);
  const spaceId = space ? space.id : null;
  const folderId = folder ? folder.id : null;

  useEffect(() => {
    dispatch(fetchFavorites());
    dispatch(fetchAllTags());
  }, [dispatch]);

  useEffect(() => {
    if (!spaceId) {
      return;
    }
    dispatch(fetchChildren(spaceId, folderId));
    dispatch(fetchDocumentsByActiveFolder(spaceId, folderId));
  }, [spaceId, folderId, dispatch]);

  const isFolder = data => data.parent !== undefined;

  const getBulkActions = ({ selectedData }) => <FileExplorerBulkActions selectedData={selectedData} />;

  const handleEdit = data => {
    if (isFolder(data)) {
      dispatch(setEditingFolderId(data.id));
      dispatch(openModal('FOLDER_EDIT'));
    } else {
      dispatch(setActiveDocumentId(data.id));
      dispatch(openModal('DOCUMENT_EDIT'));
    }
  };

  const handleClone = data => {
    if (!isFolder(data)) {
      dispatch(cloneDocument(data.id));
    }
  };

  const handleDefaultFavoriteEdit = data => {
    if (data.entityType === 'document') {
      dispatch(setActiveDocumentId(data.id));
      dispatch(setEditingFolderId(null));
    } else {
      dispatch(setEditingFolderId(data.id));
      dispatch(setActiveDocumentId(null));
    }

    dispatch(openModal('DOCUMENT_DEFAULT_FAVORITES_EDIT'));
  };

  const handleDelete = data => {
    if (isFolder(data)) {
      dispatch(setEditingFolderId(data.id));
      dispatch(openModal('FOLDER_DELETE'));
    } else {
      dispatch(setActiveDocumentId(data.id));
      dispatch(openModal('DOCUMENT_DELETE'));
    }
  };

  const handleFavoriteToggle = data => {
    if (favoriteIds.includes(data.id)) {
      dispatch(removeFavorite(data.entityType, data.id));
    } else {
      dispatch(addFavorite(data.entityType, data.id));
    }
  };

  const handleCacheOfflineToggle = data => {
    if (data.entityType === 'document') {
      if (offlineDocumentIds.includes(data.id)) {
        dispatch(uncacheDocumentOffline(data));
      } else {
        dispatch(setActiveDocumentId(data.id));
        dispatch(setSelectedData([]));
        dispatch(openModal('DOCUMENT_CACHE_OFFLINE'));
      }
    }
  };

  const handleShare = data => {
    dispatch(setSelectedData([data]));
    dispatch(openModal('DOCUMENT_SHARE'));
  };

  const tableData = [...folders, ...documents];
  const hiddenColumnProps = {
    width: 0,
    resizable: false,
    sortable: false,
    getProps: () => ({
      style: { padding: '0px', border: '0' },
    }),
    getHeaderProps: () => ({
      style: { padding: '0px', border: '0' },
    }),
  };

  return (
    <div>
      <FileExplorer
        data={tableData}
        loading={documentsAreLoading || foldersAreLoading}
        skeleton={FileExplorerTableSkeleton}
        onRowEdit={handleEdit}
        onRowClone={handleClone}
        onRowDelete={handleDelete}
        onRowFavoriteToggle={handleFavoriteToggle}
        onRowCacheOfflineToggle={handleCacheOfflineToggle}
        onDefaultFavoriteEdit={handleDefaultFavoriteEdit}
        onShare={handleShare}
        usersById={usersById}
        tagsById={tagsById}
        isAdmin={isAdmin}
        authorities={authorities}
        bulkActions={getBulkActions}
        columnsCallback={cols => [
          ...cols,
          {
            Header: 'Type',
            id: 'entityType',
            accessor: 'entityType',
            showOnMobile: true,
            ...hiddenColumnProps,
          },
          {
            Header: 'Weight',
            id: 'weight',
            accessor: 'weight',
            showOnMobile: true,
            ...hiddenColumnProps,
          },
        ]}
        defaultSorted={[
          {
            id: 'entityType',
            desc: true,
          },
          {
            id: 'weight',
            desc: false,
          },
          {
            id: 'name',
            desc: false,
          },
        ]}
      />
    </div>
  );
}

export default ClassicalFileExplorer;
